/**
 * FAQ search in the browser, over the FAQs the page already has (it replaced the
 * Algolia `<site>Faq` indexes). Every word of the query must appear in the
 * question or the answer; FAQs with more words in the question come first.
 */
export type FaqSearchItem = { title?: string; copy?: string }

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
  ndash: '–',
  mdash: '—',
  hellip: '…',
}

const decodeEntity = (name: string) => {
  if (name[0] !== '#') return NAMED_ENTITIES[name.toLowerCase()] ?? ' '
  const code = name[1] === 'x' || name[1] === 'X' ? parseInt(name.slice(2), 16) : parseInt(name.slice(1), 10)
  return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : ' '
}

/**
 * Plain, lower-case text without accents, so "Lénte" matches "lente". Only Latin
 * accents are removed: Japanese dakuten and Korean syllables stay as they are.
 * NFKC folds full-width letters and digits to their usual forms, and curly quotes
 * and dashes become the ones on a keyboard.
 */
export function normalizeSearchText(value = '') {
  return value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (_, name: string) => decodeEntity(name))
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .normalize('NFKC')
    .replace(/[\u2018\u2019\u201b\u2032]/g, "'")
    .replace(/[\u201c\u201d\u201e\u2033]/g, '"')
    .replace(/[\u2010-\u2015]/g, '-')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

/** Returns a search function over `faqs`, which are normalized once. */
export function createFaqSearch<T extends FaqSearchItem>(faqs: ReadonlyArray<T>) {
  const entries = faqs.map((faq, order) => {
    const title = normalizeSearchText(faq.title)
    return { faq, order, title, text: `${title} ${normalizeSearchText(faq.copy)}` }
  })

  return (query: string): T[] => {
    const words = normalizeSearchText(query).split(' ').filter(Boolean)
    if (!words.length) return []

    return entries
      .filter((entry) => words.every((word) => entry.text.includes(word)))
      .map((entry) => ({ ...entry, inTitle: words.filter((word) => entry.title.includes(word)).length }))
      .sort((a, b) => b.inTitle - a.inTitle || a.order - b.order)
      .map((entry) => entry.faq)
  }
}
